import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AlcmeonCardFooter } from 'src/app/shared/components/card-footer/models/alcmeon-card-footer.model';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { MediasService } from 'src/app/shared/services/medias.service';
import { urlValidator } from 'src/app/shared/validators/url.validator';
import { UserV2 } from '../../users/shared/models';
import { AlcmeonAiService } from '../alcmeon-ai.service';
import { SourceTypeEnum } from '../shared/enums';
import { AiAgent, KnowledgeDocument } from '../shared/models';

@Component({
  selector: 'app-knowledge-document-edit',
  templateUrl: './knowledge-document-edit.component.html',
  styleUrls: ['./knowledge-document-edit.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class KnowledgeDocumentEditComponent implements OnInit, OnDestroy {
  footerContent: AlcmeonCardFooter;
  isCreation: boolean;
  title: string;
  uuid: string;
  gaiaAiAgents: AiAgent[];
  gaiaAiAgentsItems: Choice[];
  sourceTypeEnum = SourceTypeEnum;
  linkDepthItems: Choice[];
  updateFrequencyItems: Choice[];
  textInputMaxLimit = 5000;
  knowledgeDoc: KnowledgeDocument;
  usersMap: Map<number, UserV2>;
  deleteFile: string;
  restoreFile: string;

  urlValidators = [Validators.required, urlValidator, Validators.pattern(/^https:\/\//)];
  textValidators = [Validators.required, Validators.maxLength(this.textInputMaxLimit)];

  // pdf
  file: File;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  maxFileSizeMB = 25;
  isFileReplaced: boolean;

  // form
  form: FormGroup = this.formBuilder.group({
    name: ['', Validators.required],
    isActive: [Validators.required],
    usedByAiAgentsIds: [[]],
    sourceType: ['', Validators.required],
    // sourceType PDF
    pdf: ['', Validators.required],
    // sourceType WEB
    url: ['', this.urlValidators],
    linkDepth: ['', Validators.required],
    updateFrequency: ['', Validators.required],
    // sourceType TEXT
    text: ['', this.textValidators],
  });

  private subscriptions = new Subscription();

  constructor(
    private alcmeonAiService: AlcmeonAiService,
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private customRouter: CustomRouter,
    private formBuilder: FormBuilder,
    private mediasService: MediasService,
    @Inject('$stateParams') private $stateParams: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {}

  ngOnInit(): void {
    this.uuid = this.$stateParams.uuid;
    this.isCreation = this.customRouter.current.data.create;
    this.title = this.isCreation
      ? this.basicService.translate('parameters/alcmeon-ai/edit-doc/create-title')
      : this.basicService.translate('parameters/alcmeon-ai/edit-doc/edit-title');
    this.topToolbarService.setTitle(
      `${this.basicService.translate('parameters/alcmeon-ai/title')} › ${this.title}`,
      null
    );
    this.usersMap = this.alcmeonContextService.getCompany().usersMap;

    // tooltip pdf
    this.deleteFile = this.basicService.translate('parameters/alcmeon-ai/edit-doc/type/pdf/tooltip/delete-file');
    this.restoreFile = this.basicService.translate('parameters/alcmeon-ai/edit-doc/type/pdf/tooltip/restore-file');

    this.initValues();

    this.subscriptions.add(
      this.form.get('sourceType').valueChanges.subscribe(type => {
        if (type === SourceTypeEnum.PDF) {
          this.enablePdf();
        } else if (type === SourceTypeEnum.WEB) {
          this.enableWeb();
        } else if (type === SourceTypeEnum.TEXT) {
          this.enableText();
        }
        this.form.updateValueAndValidity();
      })
    );
  }

  private initValues() {
    if (this.isCreation) {
      // default values
      this.form.get('isActive').setValue(false);
      this.form.get('url').setValue('https://');
      this.form.get('linkDepth').setValue(3);
      this.form.get('updateFrequency').setValue(3);
    } else {
      this.basicService.showSpinner();
      this.alcmeonAiService
        .getKnowledgeDocument(this.uuid)
        .pipe(finalize(() => this.basicService.hideSpinner()))
        .subscribe((knowledgeDocument: KnowledgeDocument) => {
          if (Object.keys(knowledgeDocument).length) {
            this.knowledgeDoc = knowledgeDocument;
            this.form.patchValue(this.knowledgeDoc);

            if (this.knowledgeDoc.sourceType === SourceTypeEnum.PDF) {
              this.form.get('pdf').setValue('pdf');
              this.isFileReplaced = false;
            }

            // card footer
            this.updateFooter(this.knowledgeDoc);
          } else {
            this.basicService.toastAlert('parameters/alcmeon-ai/edit-doc/no-doc-error');
            this.goToKnowledgeDocuments();
          }
        });
    }

    this.alcmeonAiService.getAiAgents({ sort: { active: 'name', direction: 'asc' } }).subscribe(aiAgents => {
      this.gaiaAiAgents = (aiAgents.body || []).filter(aiAgents => aiAgents.type === 0 && aiAgents.isActive);
      this.gaiaAiAgentsItems = this.gaiaAiAgents.map(agent => ({ id: agent.id, label: agent.name }));
    });

    this.linkDepthItems = Array.from({ length: 6 }).map((_, id) => ({ id, label: `${id}` }));
    this.updateFrequencyItems = [
      { id: 0, label: this.basicService.translate('parameters/alcmeon-ai/edit-doc/frequency/never') },
      { id: 1, label: this.basicService.translate('parameters/alcmeon-ai/edit-doc/frequency/daily') },
      { id: 2, label: this.basicService.translate('parameters/alcmeon-ai/edit-doc/frequency/weekly') },
      { id: 3, label: this.basicService.translate('parameters/alcmeon-ai/edit-doc/frequency/monthly') },
    ];
  }

  private updateFooter(knowledgeDoc: KnowledgeDocument) {
    this.footerContent = {
      createdBy: this.usersMap.get(knowledgeDoc.createdBy)?.realname,
      createdAt: knowledgeDoc.createdAt,
      modifiedBy: this.usersMap.get(knowledgeDoc.updatedBy)?.realname,
      modifiedAt: knowledgeDoc.updatedAt,
    };
  }

  private enablePdf() {
    this.disablePdfWebText();
    this.form.get('pdf').enable({ onlySelf: true });
  }

  private enableWeb() {
    this.disablePdfWebText();
    this.form.get('url').enable({ onlySelf: true });
    this.form.get('linkDepth').enable({ onlySelf: true });
    this.form.get('updateFrequency').enable({ onlySelf: true });
  }

  private enableText() {
    this.disablePdfWebText();
    this.form.get('text').enable({ onlySelf: true });
  }

  private disablePdfWebText() {
    this.form.get('pdf').disable({ onlySelf: true });
    this.form.get('url').disable({ onlySelf: true });
    this.form.get('linkDepth').disable({ onlySelf: true });
    this.form.get('updateFrequency').disable({ onlySelf: true });
    this.form.get('text').disable({ onlySelf: true });
  }

  goToKnowledgeDocuments() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.alcmeon-ai.knowledge-documents');
  }

  forceContentRefresh() {
    this.basicService.showSpinner();
    this.alcmeonAiService
      .updateKnowledgeDocument(this.uuid, this.form.value)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        complete: () => this.basicService.toastSuccess('parameters/alcmeon-ai/edit-doc/force-refresh-success'),
      });
  }

  /**
   * Real action on file input
   * Load pdf in form, not yet sended to BE
   */
  loadPdf($event) {
    if ($event.target.files[0]) {
      if ($event.target.files[0].type !== 'application/pdf') {
        this.basicService.toastAlert('parameters/alcmeon-ai/edit-doc/type/pdf/error-type');
        return;
      }

      if ($event.target.files[0].size > this.maxFileSizeMB * 1024 * 1024) {
        this.basicService.toastAlert('parameters/alcmeon-ai/edit-doc/type/pdf/error-size');
        return;
      }

      this.file = $event.target.files[0];
      this.form.get('pdf').setValue(this.file);
      this.form.get('pdf').markAsDirty();
      if (!this.isCreation) {
        this.isFileReplaced = true;
      }
    }
  }

  /**
   * Action on custom button to overload appareance of file input (real input is hidden)
   */
  clickToLoadPdf() {
    this.fileInput.nativeElement.value = ''; // allow selecting the same file again
    this.fileInput.nativeElement.click();
  }

  deletePdf() {
    if (this.isCreation) {
      this.form.get('pdf').setValue(null);
    } else {
      this.isFileReplaced = false;
      this.form.get('pdf').setValue('pdf');
      this.form.get('pdf').markAsPristine();
    }
    this.file = null;
  }

  downloadPdf() {
    this.basicService.showSpinner();
    this.alcmeonAiService
      .getPdf(this.uuid)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(data => {
        const { content, contenType, contentFilename } = data;
        this.mediasService.downloadBinaryBase64(content, contenType, contentFilename);
      });
  }

  save() {
    this.basicService.showSpinner();
    if (this.isCreation) {
      const creation =
        this.form.get('sourceType').value === SourceTypeEnum.PDF
          ? this.alcmeonAiService.createKnowledgeDocumentPdf(this.form.value, this.file)
          : this.alcmeonAiService.createKnowledgeDocument(this.form.value);

      creation.pipe(finalize(() => this.basicService.hideSpinner())).subscribe({
        complete: () => this.basicService.toastSuccess('parameters/alcmeon-ai/edit-doc/create-success'),
        next: () => {
          this.goToKnowledgeDocuments();
        },
      });
    } else {
      const edition =
        this.form.get('sourceType').value === SourceTypeEnum.PDF
          ? this.alcmeonAiService.updateKnowledgeDocumentPdf(this.uuid, this.form.value, this.file)
          : this.alcmeonAiService.updateKnowledgeDocument(this.uuid, this.form.value);

      edition.pipe(finalize(() => this.basicService.hideSpinner())).subscribe({
        complete: () => this.basicService.toastSuccess('parameters/alcmeon-ai/edit-doc/edit-success'),
        next: knowledgeDoc => {
          this.knowledgeDoc = knowledgeDoc;
          if (this.isFileReplaced) {
            this.file = null;
          }
          this.updateFooter(knowledgeDoc);
        },
      });
    }
    this.form.markAsPristine();
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
